import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Award, ChevronDown, Coins, LogOut, User } from 'lucide-react';
import { useOrganization } from '../../features/organization/OrganizationContext';
import { hasModule } from '../../shared/hooks/useModuleAccess';
import { PersonAvatar } from '../../shared/ui/PersonAvatar';
import { clearImpersonation, endImpersonation, isImpersonating as readImpersonating } from '../../shared/api/impersonation';
import { signOutMediaSession } from '../../shared/api/auth';
import { useI18n } from '../../i18n/I18nProvider';

interface Props {
  user: any;
}

// Shared across every page — including ones that render their own compact
// topbar instead of the global AppBar (Learning Hub, Admin Center) — so
// profile access and sign-out never silently disappear on a given route.
// Self-contained (org context, impersonation state, logout) so any page can
// drop it in with just the current user, no prop-drilling from AppShell.
export default function UserMenu({ user }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { orgEnabledModules } = useOrganization();

  // Certificates live in Learning; with Learning off the page answers 403, so
  // the menu does not offer it -- the same rule ModuleRoute and the sidebar use.
  const canSeeCertificates = (!Array.isArray(orgEnabledModules) || orgEnabledModules.includes('learning'))
    && hasModule(user, 'learning');
  const isImpersonating = readImpersonating();

  const handleExitImpersonation = () => endImpersonation('ended');

  // Full reload rather than an SPA navigate — this can be embedded on any page,
  // so there's no single parent component whose "current user" state it could
  // reset directly; a reload guarantees every stale bit of session state clears.
  const handleLogout = () => {
    signOutMediaSession();
    localStorage.removeItem('token');
    localStorage.removeItem('activeOrgId');
    clearImpersonation();
    window.location.href = '/login';
  };

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (!user) return null;

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="user-menu__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user.name || user.email}
      >
        <PersonAvatar className="user-menu__avatar" name={user.name || user.email} src={user.avatar} seed={user.id} size={32} />
        {/* The first name only (spec 4.2); the full name and email head the menu. */}
        <span className="user-menu__name" aria-hidden="true">
          {(user.name || user.email || '').split(/\s+/)[0]}
        </span>
        <ChevronDown
          size={14}
          className="user-menu__chevron"
          aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      <div
        className={`user-menu__panel${open ? ' user-menu__panel--open' : ''}`}
        role="menu"
        aria-label={t('edDesign.userMenu.label')}
      >
        <div className="user-menu__header">
          <PersonAvatar name={user.name || user.email} src={user.avatar} seed={user.id} size={32} />
          <div className="user-menu__header-text">
            <div className="user-menu__header-name">{user.name}</div>
            <div className="user-menu__header-email">{user.email}</div>
          </div>
        </div>

        <button
          className="user-menu__item"
          role="menuitem"
          onClick={() => { setOpen(false); navigate('/profile'); }}
        >
          <User size={16} aria-hidden="true" />
          {t('edDesign.userMenu.profile')}
        </button>
        {canSeeCertificates && (
          <button
            className="user-menu__item"
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/profile/certificates'); }}
          >
            <Award size={16} aria-hidden="true" />
            {t('edDesign.userMenu.certificates')}
          </button>
        )}
        <button
          className="user-menu__item"
          role="menuitem"
          onClick={() => { setOpen(false); navigate('/points'); }}
        >
          <Coins size={16} aria-hidden="true" />
          {t('edDesign.userMenu.points')}
        </button>

        <div className="user-menu__divider" />

        <button
          className="user-menu__item user-menu__item--danger"
          role="menuitem"
          onClick={isImpersonating ? handleExitImpersonation : handleLogout}
        >
          <LogOut size={16} aria-hidden="true" />
          {isImpersonating ? t('impersonation.back') : t('edDesign.userMenu.signOut')}
        </button>
      </div>
    </div>
  );
}
